"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "~/utils/fontsConfig";
import { usePermissionsContext, useWhiteLabel } from "~/app/providers";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";

interface Tab {
    value: string;
    label: string;
    path: string;
    requiredPermission?: (keyof UserPermissions)[];
}

export const TabsWrapper = ({ eventSlug }: { eventSlug: string }) => {
    const pathname = usePathname();
    const whiteLabelContext = useWhiteLabel();
    const customization = whiteLabelContext?.customization;
    const whiteLabel = whiteLabelContext?.whiteLabelProps;
    const permissions = usePermissionsContext();

    const basePath = whiteLabel?.slug
        ? `/beta/${whiteLabel.slug}/events/${eventSlug}`
        : `/beta/events/${eventSlug}`;

    const tabs: Tab[] = [
        {
            value: "overview",
            label: "Overview",
            path: `${basePath}/manage`,
        },
        {
            value: "hosts",
            label: "Hosts",
            path: `${basePath}/manage/hosts`,
        },
        {
            value: "guests",
            label: "Guests",
            path: `${basePath}/manage/guests`,
            requiredPermission: ["isHost", "isManager", "isAdmin"],
        },
        {
            value: "messaging",
            label: "Messaging",
            path: `${basePath}/manage/messages`,
            requiredPermission: ["isHost", "isManager", "isAdmin"],
        },
        {
            value: "digital-badges",
            label: "Digital Badges",
            path: `${basePath}/manage/digital-badges`,
            requiredPermission: ["isHost", "isAdmin", "isManager"],
        },
        {
            value: "registration",
            label: "Registration",
            path: `${basePath}/manage/registration`,
            requiredPermission: ["isManager", "isAdmin", "isHost"],
        },
        {
            value: "settings",
            label: "Settings",
            path: `${basePath}/manage/settings`,
            requiredPermission: ["isManager", "isAdmin", "isHost"],
        },
        {
            value: "promotion",
            label: "Promotion",
            path: `${basePath}/manage/promotion`,
            requiredPermission: ["isManager", "isAdmin", "isHost"],
        },
        {
            value: "more",
            label: "More",
            path: `${basePath}/manage/more`,
            requiredPermission: ["isManager", "isAdmin", "isHost"],
        },
    ];

    const visibleTabs = tabs.filter((tab) => {
        if (!tab.requiredPermission) return true;

        return tab?.requiredPermission?.some(
            (permission) => permissions[permission],
        );
    });

    const getCurrentTab = (path: string): string => {
        const tab = visibleTabs.find((t) => t.path === path);
        return tab?.value ?? "overview";
    };

    const currentTab = getCurrentTab(pathname ?? "");

    return (
        <div className="flex w-full max-w-[100vw] justify-start gap-2 overflow-x-scroll whitespace-nowrap pl-4 scrollbar-hide">
            {visibleTabs.map((tab) => (
                <Link
                    key={tab.path}
                    href={{ pathname: tab.path }}
                    className="shrink-0"
                >
                    <div
                        className={cn(
                            "shrink-0 px-4 py-3 text-sm font-medium transition-colors",
                            "hover:opacity-80",
                            currentTab === tab.value
                                ? "border-b-2 border-b-v3-clover opacity-100"
                                : "opacity-50",
                        )}
                    >
                        {tab.label}
                    </div>
                </Link>
            ))}
        </div>
    );
};
